import React, { useEffect, useRef, useState } from 'react'
import type { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import type { GraphLike, TypeIdResolver } from '../lib/exporter'
import { ACCEPTED_FILE_EXTENSIONS } from '../lib/constants'
import styles from './ExcelMenu.module.css'

interface Props {
  graph: GraphLike | null
  objectTypes: ObjectType[]
  mapName: string
  /**
   * Optional callback that resolves an element to its ObjectType id when the
   * element itself doesn't carry one (CSV-imported entities whose typeId lives
   * in the entity-mapping or layer-type-mapping stores). Without this, those
   * elements get silently dropped from the export.
   */
  resolveTypeId?: TypeIdResolver
  /** Notify host about toast-worthy events. Optional. */
  onMessage?: (kind: 'info' | 'success' | 'error', text: string) => void
  /** Invoked after a file is selected. Host runs parse → validate → open modal. */
  onFileSelected: (file: File) => void
  /** 객체 수정 시트(화면 우측 절반) 열기 — 제공될 때만 메뉴 항목 표시. */
  onEditObjects?: () => void
  disabled?: boolean
}

/**
 * Toolbar entry exposing the three Excel actions:
 *   1) 현재 데이터 내보내기 (xlsx download)
 *   2) 빈 템플릿 다운로드
 *   3) Excel 업로드 (delegates parse/validate to host via `onFileSelected`)
 *
 * The component owns the file <input>, dropdown open/close state, and the
 * dynamic import of the exporter (so the xlsx library is not loaded until
 * the user actually triggers an action).
 */
export const ExcelMenu: React.FC<Props> = ({
  graph,
  objectTypes,
  mapName,
  resolveTypeId,
  onMessage,
  onFileSelected,
  onEditObjects,
  disabled,
}) => {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [open])

  const handleExportCurrent = async () => {
    if (!graph) return
    setOpen(false)
    setBusy(true)
    try {
      const { exportMapToExcel } = await import('../lib/exporter')
      await exportMapToExcel(graph, objectTypes, mapName, undefined, resolveTypeId)
      onMessage?.('success', 'Excel 내보내기 완료')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      onMessage?.('error', `내보내기 실패: ${msg}`)
    } finally {
      setBusy(false)
    }
  }

  const handleExportTemplate = async () => {
    setOpen(false)
    setBusy(true)
    try {
      const { exportEmptyTemplate } = await import('../lib/exporter')
      await exportEmptyTemplate(objectTypes)
      onMessage?.('success', '템플릿 다운로드 완료')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      onMessage?.('error', `템플릿 다운로드 실패: ${msg}`)
    } finally {
      setBusy(false)
    }
  }

  const handleUploadClick = () => {
    setOpen(false)
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset so the same file can be picked twice in a row.
    e.target.value = ''
    if (!file) return
    onFileSelected(file)
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(o => !o)}
        disabled={disabled || busy}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Excel Import/Export"
      >
        📊 Excel
        <span className={styles.caret} aria-hidden>▾</span>
      </button>

      {open && (
        <div className={styles.menu} role="menu">
          <button
            type="button"
            className={styles.item}
            role="menuitem"
            onClick={handleExportCurrent}
            disabled={!graph || objectTypes.length === 0}
          >
            <span className={styles.itemIcon}>⬇</span>
            <span className={styles.itemLabel}>
              <strong>Excel 내보내기</strong>
              <span className={styles.itemSub}>현재 맵 데이터를 .xlsx로</span>
            </span>
          </button>
          <button
            type="button"
            className={styles.item}
            role="menuitem"
            onClick={handleExportTemplate}
            disabled={objectTypes.length === 0}
          >
            <span className={styles.itemIcon}>📄</span>
            <span className={styles.itemLabel}>
              <strong>빈 템플릿 다운로드</strong>
              <span className={styles.itemSub}>헤더만 있는 .xlsx</span>
            </span>
          </button>
          <div className={styles.separator} />
          <button
            type="button"
            className={styles.item}
            role="menuitem"
            onClick={handleUploadClick}
            disabled={!graph || objectTypes.length === 0}
          >
            <span className={styles.itemIcon}>⬆</span>
            <span className={styles.itemLabel}>
              <strong>Excel 업로드</strong>
              <span className={styles.itemSub}>id 매칭으로 일괄 수정</span>
            </span>
          </button>
          {onEditObjects && (
            <>
              <div className={styles.separator} />
              <button
                type="button"
                className={styles.item}
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  onEditObjects()
                }}
                disabled={!graph}
              >
                <span className={styles.itemIcon}>📋</span>
                <span className={styles.itemLabel}>
                  <strong>객체 수정</strong>
                  <span className={styles.itemSub}>우측 분할 시트에서 즉시 수정</span>
                </span>
              </button>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_EXTENSIONS.join(',')}
        className={styles.hiddenInput}
        onChange={handleFileChange}
      />
    </div>
  )
}
